import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Clock, 
  MapPin, 
  ChevronRight, 
  Loader2,
  Calendar,
  Target,
  RefreshCw,
  LogOut,
  FileText,
  Camera,
  Sparkles,
  Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// UpcomingList component to avoid inline array mapping
function UpcomingList({ appointments, formatDate, formatTime, onAssetClick }) {
  const items = [];
  for (let i = 0; i < appointments.length; i++) {
    const apt = appointments[i];
    items.push(
      <div 
        key={apt.id || i}
        className="py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 -mx-4 px-4 transition-colors"
        onClick={() => window.location.href = apt.prep_url}
        data-testid={`appointment-row-${i}`}
      >
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">
            {apt.customer_name || 'Kunde'}
          </p>
          <p className="text-sm text-slate-500">
            {formatDate(apt.scheduled_start)} kl. {formatTime(apt.scheduled_start)}
          </p>
        </div>
        
        <div className="flex items-center gap-1 ml-2">
          {apt.has_order && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onAssetClick(apt.hubhus_booking_id);
              }}
              variant="ghost"
              size="sm"
              className="text-teal-500 hover:text-teal-600 hover:bg-teal-50"
              data-testid={`asset-apt-${i}`}
            >
              <FileText className="h-4 w-4" />
            </Button>
          )}
          <ChevronRight className="h-5 w-5 text-slate-400" />
        </div>
      </div>
    );
  }
  return <div className="divide-y divide-slate-100">{items}</div>;
}

const HomePage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNextAppointments();
  }, [token]);

  const fetchNextAppointments = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/appointments/next`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(response.data.appointments || []);
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    try {
      const dt = new Date(isoString);
      return dt.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const formatDate = (isoString) => {
    try {
      const dt = new Date(isoString);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (dt.toDateString() === today.toDateString()) return 'I dag';
      if (dt.toDateString() === tomorrow.toDateString()) return 'I morgen';
      
      return dt.toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
      return '';
    }
  };

  const handleAssetOrderClick = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/booking/${bookingId}/asset-order-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.asset_pdf_url) {
        // Open PDF in new tab
        window.open(response.data.asset_pdf_url, '_blank');
      } else if (response.data.has_order) {
        // PDF URL not yet available
        alert(`Ordre ${response.data.order_id} fundet. PDF er ikke tilgængelig endnu.`);
      } else {
        alert('Ingen ordre fundet for denne booking.');
      }
    } catch (err) {
      console.error('Failed to get asset order URL:', err);
      alert('Kunne ikke hente ordre-information.');
    }
  };

  const formatArrival = (minutes) => {
    if (minutes === null || minutes === undefined) return null;
    if (minutes < 1) return 'Nu';
    if (minutes < 60) return `om ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `om ${hours}t`;
    return `om ${hours}t ${mins}m`;
  };

  const nextAppointment = appointments[0];
  const upcomingAppointments = appointments.slice(1);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - with safe area padding for iOS PWA */}
      <div className="bg-slate-900 text-white px-4 py-6 safe-top">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-outfit">
              Hej, {user?.name?.split(' ')[0] || 'Sælger'}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {appointments.length > 0 
                ? `Du har ${appointments.length} kommende besøg`
                : 'Ingen kommende besøg'}
            </p>
          </div>
          <Button
            onClick={() => { logout(); navigate('/login'); }}
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Next Appointment - Featured */}
        {nextAppointment ? (
          <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-900 to-slate-800 text-white overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Badge className="bg-teal-500 text-white border-0">
                  Næste besøg
                </Badge>
                {nextAppointment.arrival_minutes !== null && (
                  <span className="text-teal-400 text-sm font-medium">
                    {formatArrival(nextAppointment.arrival_minutes)}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <h2 className="text-xl font-bold mb-1">
                {nextAppointment.customer_name || 'Kunde'}
              </h2>
              
              <div className="flex items-center gap-4 text-slate-300 text-sm mb-4">
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {formatDate(nextAppointment.scheduled_start)} kl. {formatTime(nextAppointment.scheduled_start)}
                </span>
              </div>
              
              {(nextAppointment.address || nextAppointment.city) && (
                <p className="text-slate-400 text-sm flex items-center gap-1 mb-4">
                  <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                  {[nextAppointment.address, nextAppointment.city].filter(Boolean).join(', ')}
                </p>
              )}

              <Button
                onClick={() => window.location.href = nextAppointment.prep_url}
                className="w-full h-14 bg-teal-500 hover:bg-teal-600 text-white font-semibold text-lg"
                data-testid="prepare-now-btn"
              >
                <Target className="h-5 w-5 mr-2" />
                Forbered nu
              </Button>
              
              {/* ASSET Order Link */}
              {nextAppointment.has_order && (
                <Button
                  onClick={() => handleAssetOrderClick(nextAppointment.hubhus_booking_id)}
                  variant="outline"
                  className="w-full mt-2 border-teal-500 text-teal-400 hover:bg-teal-500/10"
                  data-testid="asset-order-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  ASSET Ordre
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-8 text-center">
              <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Ingen kommende besøg</h3>
              <p className="text-slate-500 text-sm">
                Dine næste aftaler vises her
              </p>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Appointments List */}
        {upcomingAppointments.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium text-slate-700">
                Kommende besøg
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <UpcomingList 
                appointments={upcomingAppointments} 
                formatDate={formatDate} 
                formatTime={formatTime}
                onAssetClick={handleAssetOrderClick}
              />
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          {/* Calendar - available for all roles */}
          <Button
            onClick={() => navigate('/calendar')}
            variant="outline"
            className="h-14 flex-col gap-1"
            data-testid="view-calendar-btn"
          >
            <Calendar className="h-5 w-5" />
            <span className="text-xs">Kalender</span>
          </Button>
          
          {/* Spørg Gardinbussen - available for all roles */}
          <Button
            onClick={() => navigate('/produktviden')}
            variant="outline"
            className="h-14 flex-col gap-1"
            data-testid="product-knowledge-btn"
          >
            <Sparkles className="h-5 w-5" />
            <span className="text-xs">Spørg AI</span>
          </Button>
          
          {/* Image upload - available for all roles */}
          <Button
            onClick={() => navigate('/upload')}
            variant="outline"
            className="h-14 flex-col gap-1"
            data-testid="upload-image-btn"
          >
            <Camera className="h-5 w-5" />
            <span className="text-xs">Upload Billede</span>
          </Button>
          
          {/* Image gallery - available for all roles */}
          <Button
            onClick={() => navigate('/billeder')}
            variant="outline"
            className="h-14 flex-col gap-1"
            data-testid="view-gallery-btn"
          >
            <ImageIcon className="h-5 w-5" />
            <span className="text-xs">Billeder</span>
          </Button>
        </div>
        
        {/* Refresh button - separate row */}
        <Button
          onClick={fetchNextAppointments}
          variant="outline"
          className="w-full h-12"
          data-testid="refresh-appointments-btn"
        >
          <RefreshCw className="h-5 w-5 mr-2" />
          Opdater aftaler
        </Button>
      </div>
    </div>
  );
};

export default HomePage;
